"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer({
  isMobile = false,
  onItemClick
}: {
  isMobile?: boolean;
  onItemClick?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="8017" className={cn("flex gap-4", isMobile ? "flex-col items-center" : "flex-row")}>
        {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-20 bg-primary/10 animate-pulse rounded-none" />)}
      </div>;
  }
  if (isMobile) {
    return <nav className="flex flex-col items-center gap-8">
        {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-2xl font-bold uppercase tracking-[0.2em] text-foreground hover:text-primary transition-colors duration-300" onClick={onItemClick} data-index={i}>
            {item.label}
          </Link>)}
      </nav>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative inline-flex h-9 w-max items-center justify-center rounded-none bg-transparent px-2 py-2 text-[11px] font-bold uppercase tracking-[0.2em] text-foreground transition-colors hover:text-primary focus:text-primary focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/10 data-[state=open]:bg-accent/10">
                {item.label}
                <span className="absolute inset-x-0 -bottom-1 h-[1px] scale-x-0 bg-primary transition-transform duration-500 group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function GlobalNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSpanish, setIsSpanish] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const toggleLanguage = () => setIsSpanish(!isSpanish);
  return <header id="hlc-navigation" className={cn("sticky top-0 left-0 z-50 w-full transition-all duration-700 border-b border-transparent dark bg-background text-foreground", isScrolled ? "bg-background/70 backdrop-blur-md border-primary/10 shadow-2xl" : "bg-background")}>
      <div className="container mx-auto px-6 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage" className="block">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1591502/img/sound_it_out_logo_white.png" alt="Handling and Logistic Company (HLC)" className="h-8 w-auto object-contain brightness-110 contrast-125" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="16">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-6">
          {}
          <div className="hidden md:flex items-center gap-3">
            <span className={cn("text-[10px] font-bold tracking-widest transition-colors cursor-pointer font-mono", !isSpanish ? "text-primary" : "text-muted-foreground hover:text-foreground")} onClick={() => setIsSpanish(false)}>
              EN
            </span>
            <Switch checked={isSpanish} onCheckedChange={setIsSpanish} className="data-[state=checked]:bg-primary/20 data-[state=unchecked]:bg-primary/20 border-primary/30" aria-label="Toggle Language" />
            <span className={cn("text-[10px] font-bold tracking-widest transition-colors cursor-pointer font-mono", isSpanish ? "text-primary" : "text-muted-foreground hover:text-foreground")} onClick={() => setIsSpanish(true)}>
              FR
            </span>
          </div>

          {}
          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary hover:bg-transparent" onClick={() => setIsMobileMenuOpen(true)}>
            <Menu className="h-6 w-6" />
            <span className="sr-only">Open menu</span>
          </Button>
        </div>
      </div>

      {}
      <div className={cn("fixed inset-0 z-[60] bg-background/95 backdrop-blur-2xl transition-transform duration-500 md:hidden flex flex-col", isMobileMenuOpen ? "translate-x-0" : "translate-x-full")}>
        <div className="container mx-auto px-6 h-20 flex items-center justify-between border-b border-primary/10">
          <Link to="/HomePage" onClick={() => setIsMobileMenuOpen(false)}>
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1591502/img/sound_it_out_logo_white.png" alt="Handling and Logistic Company (HLC)" className="h-8 w-auto object-contain brightness-110" />
          </Link>
          <Button variant="ghost" size="icon" className="text-foreground hover:text-primary hover:bg-transparent" onClick={() => setIsMobileMenuOpen(false)}>
            <X className="h-6 w-6" />
            <span className="sr-only">Close menu</span>
          </Button>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center gap-12 p-6">
          <MenuProvider menu_id="16">
            <HeaderMenuConsumer isMobile onItemClick={() => setIsMobileMenuOpen(false)} />
          </MenuProvider>

          {}
          <div className="flex items-center gap-4 mt-8">
            <span className={cn("text-xs font-bold tracking-[0.2em] transition-colors font-mono", !isSpanish ? "text-primary" : "text-muted-foreground")} onClick={() => setIsSpanish(false)}>
              EN
            </span>
            <Switch checked={isSpanish} onCheckedChange={setIsSpanish} className="scale-125 data-[state=checked]:bg-primary/20 data-[state=unchecked]:bg-primary/20" />
            <span className={cn("text-xs font-bold tracking-[0.2em] transition-colors font-mono", isSpanish ? "text-primary" : "text-muted-foreground")} onClick={() => setIsSpanish(true)}>
              FR
            </span>
          </div>
        </div>
      </div>
    </header>;
}